import React, { useRef } from 'react'
import { Presentation, Maximize } from 'lucide-react'
import { genId } from '../lib/id'
import type { PptxCard } from '../types/LessonDoc'
import type { CardDef, CardEditorProps, CardPreviewProps } from './types'

// Lấy FILE_ID + loại (slides/drive) từ link Google Drive / Google Slides.
function parseDrive(url: string): { id: string; kind: 'slides' | 'drive' } | null {
  if (!url) return null
  const isSlides = url.includes('/presentation/')
  const m =
    url.match(/\/d\/([\w-]+)/) ||
    url.match(/[?&]id=([\w-]+)/) ||
    url.match(/\/file\/d\/([\w-]+)/)
  if (m) return { id: m[1], kind: isSlides ? 'slides' : 'drive' }
  return null
}

function embedUrl(info: { id: string; kind: 'slides' | 'drive' }): string {
  if (info.kind === 'slides') {
    return `https://docs.google.com/presentation/d/${info.id}/embed?start=false&loop=false&delayms=60000`
  }
  return `https://drive.google.com/file/d/${info.id}/preview`
}

const Editor: React.FC<CardEditorProps> = ({ card, onChange }) => {
  const c = card as PptxCard
  return (
    <div className="space-y-3">
      <div className="p-3 rounded-lg bg-orange-50 border border-orange-100 text-orange-700 text-sm space-y-1">
        <p className="font-bold">Cách lấy link trình chiếu:</p>
        <ol className="list-decimal pl-5 space-y-0.5">
          <li>Tải file <strong>.pptx</strong> lên Google Drive (hoặc mở bằng Google Slides để có hiệu ứng).</li>
          <li>Bấm <strong>Chia sẻ</strong> → <strong>Bất kỳ ai có đường liên kết</strong> → Người xem.</li>
          <li>Copy link và dán vào ô dưới.</li>
        </ol>
      </div>
      <div>
        <label className="text-xs font-bold text-gray-500 uppercase">Link Google Drive / Google Slides</label>
        <input
          className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
          value={c.driveUrl || ''}
          onChange={(e) => onChange({ driveUrl: e.target.value })}
          placeholder="https://docs.google.com/presentation/d/... hoặc https://drive.google.com/file/d/..."
        />
      </div>
      {c.driveUrl && !parseDrive(c.driveUrl) && (
        <p className="text-xs text-red-600">Link chưa hợp lệ — cần là link Google Drive/Slides có dạng .../d/ID/...</p>
      )}
    </div>
  )
}

const Preview: React.FC<CardPreviewProps> = ({ card }) => {
  const c = card as PptxCard
  const wrapRef = useRef<HTMLDivElement>(null)
  const info = c.driveUrl ? parseDrive(c.driveUrl) : null

  const goFullscreen = () => {
    const el = wrapRef.current
    if (el?.requestFullscreen) el.requestFullscreen()
  }

  if (info) {
    return (
      <div ref={wrapRef} className="relative bg-black rounded-lg overflow-hidden">
        <button
          onClick={goFullscreen}
          title="Toàn màn hình"
          className="absolute top-2 right-2 z-10 flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/90 text-gray-700 text-xs font-bold hover:bg-white shadow"
        >
          <Maximize size={14} /> Toàn màn hình
        </button>
        <iframe
          title={c.title || 'Bài trình chiếu'}
          src={embedUrl(info)}
          className="w-full"
          style={{ height: 460, border: 0 }}
          allowFullScreen
        />
      </div>
    )
  }

  // Fallback: PDF/ảnh (nếu dùng hướng soffice sau này)
  if (c.images && c.images.length > 0) {
    return (
      <div className="space-y-3">
        {c.images.map((src, i) => (
          <img key={i} src={src} alt={`slide-${i + 1}`} className="w-full rounded-lg border border-gray-200" />
        ))}
      </div>
    )
  }
  if (c.pdfSrc) {
    return <iframe title={c.title || 'Slide'} src={c.pdfSrc} className="w-full rounded-lg border border-gray-200" style={{ height: 600 }} />
  }
  return (
    <div className="p-4 rounded-lg bg-gray-50 border border-dashed border-gray-200 text-gray-400 text-sm text-center">
      Dán link Google Drive/Slides để trình chiếu.
    </div>
  )
}

export const pptxCardDef: CardDef = {
  type: 'pptx',
  label: 'PowerPoint',
  description: 'Trình chiếu qua Google Drive/Slides',
  icon: Presentation,
  color: 'bg-orange-50 text-orange-600',
  createDefault: (): PptxCard => ({ id: genId(), type: 'pptx' }),
  Editor,
  Preview,
}
